/**
 * E1 — Purchase claims, the drill-down behind the Revenue card.
 *
 * A NESTED ROUTE (`revenue/claims`) AND NO RAIL ENTRY, on purpose. This is one
 * question about one card rather than a screen of its own, so it is reached
 * from that card the way `books/:bookId` is reached from a row.
 * `__e2e__/navigation.journey.test.tsx` compares the Sidebar's slugs with this
 * section's routes using an `[a-z-]+` pattern, which a path containing a slash
 * cannot match — so a nested route is outside that comparison by construction
 * and cannot move the rail count. It still gets a render-walk case there.
 *
 * ⚠️ THE LAPSED ROW IS THE WHOLE POINT OF THIS GRID.
 *
 * claimService applies expiry LAZILY — it flips `pending_claim` to `expired`
 * the next time it looks a row up, and there is no sweep (migration 446). So a
 * claim whose token ran out three weeks ago is, in the table, still stored as
 * `pending_claim`. The server evaluates the same predicate at read time and
 * ships BOTH: `stored_status` (untouched) and `status` (effective), plus
 * `expired_by_time` saying they differ.
 *
 * This page renders the EFFECTIVE status, because that is the true one — a
 * grid showing `stored_status` would present lapsed money as still claimable.
 * It renders the disagreement too, as an outlined "expired (lapsed)" chip
 * carrying the server's own `expiry_note`, so nobody reads the row as a bug or
 * goes looking for the write that "should" have happened. THIS SCREEN NEVER
 * WRITES: the wedding service owns that transition, inside the transaction
 * that re-checks both the status and the deadline.
 *
 * Server-side paging, sorting and filtering, as on Books and Couples — the
 * grid is a view over a query, never a client-side re-sort of one page.
 *
 * ZERO HEX.
 */

import React from 'react';
import {
  Box, Button, Chip, InputAdornment, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import DataTable, { Column } from '../../common/DataTable';
import EmptyState from '../../common/EmptyState';
import { WeddingTierChip } from '../WeddingChips';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type { ClaimRow, ClaimStatus, ClaimsResponse, Tier } from '../weddingApi';
import { TIERS, TIER_META, centsToUsd, num, shortDate } from '../weddingFormat';

/** Mirrors CLAIM_SORT_COLUMNS in weddingAdmin.js. Anything else is ignored server-side. */
const SORTABLE = new Set([
  'created_at', 'amount_cents', 'claim_token_expires_at', 'claimed_at', 'tier', 'buyer_email', 'status',
]);

type StatusFilter = ClaimStatus | 'all';

const STATUS_PILLS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending_claim', label: 'Still claimable' },
  { key: 'claimed', label: 'Claimed' },
  { key: 'expired', label: 'Expired' },
  { key: 'refunded', label: 'Refunded' },
];

/** MUI palette keys only — the effective status, never the stored one. */
const STATUS_CHIP: Record<ClaimStatus, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  pending_claim: { label: 'Still claimable', color: 'warning' },
  claimed: { label: 'Claimed', color: 'success' },
  expired: { label: 'Expired', color: 'error' },
  refunded: { label: 'Refunded', color: 'default' },
};

const WeddingClaimsPage: React.FC = () => {
  const [data, setData] = React.useState<ClaimsResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<StatusFilter>('all');
  const [tier, setTier] = React.useState<Tier | 'all'>('all');

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [sortBy, setSortBy] = React.useState('created_at');
  const [sortOrder, setSortOrder] = React.useState<'ASC' | 'DESC'>('DESC');

  // 350 ms, matching WeddingBooksPage and WeddingCouplesPage. The page reset
  // rides along in the same state update so a new search is ONE request, not a
  // request for page 4 of the new result set followed by a request for page 1.
  React.useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await weddingApi.getClaims({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: status === 'all' ? undefined : status,
        tier: tier === 'all' ? undefined : tier,
        sortBy: SORTABLE.has(sortBy) ? sortBy : undefined,
        sortOrder,
      }));
    } catch (e) {
      setError(e instanceof WeddingApiError ? e.message : 'Could not load purchase claims.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, status, tier, sortBy, sortOrder]);

  React.useEffect(() => { load(); }, [load]);

  // Any filter change puts you back on page 1: page 4 of a narrower result set
  // is an empty grid that looks like an empty database.
  const changeStatus = (next: StatusFilter) => { setStatus(next); setPage(0); };
  const changeTier = (next: Tier | 'all') => { setTier(next); setPage(0); };

  const counts = data ? data.status_counts : null;
  const summary = data ? data.summary : null;

  const columns: Column<ClaimRow>[] = [
    {
      id: 'buyer_email',
      label: 'Buyer',
      sortable: true,
      render: (row) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2">{row.buyer_email || 'No email recorded'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.buyer_name || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'tier',
      label: 'Tier',
      sortable: true,
      render: (row) => <WeddingTierChip tier={row.tier} />,
    },
    {
      id: 'amount_cents',
      label: 'Amount',
      sortable: true,
      align: 'right',
      render: (row) => <Typography variant="body2">{centsToUsd(row.amount_cents)}</Typography>,
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => {
        /*
         * THE LOAD-BEARING BRANCH. The stored column and the effective status
         * disagree exactly when a token lapsed and nobody has looked the row
         * up since. Outlined rather than filled, and labelled "(lapsed)", so
         * it is legible as a row whose stored value has not caught up — and
         * the tooltip is the SERVER's sentence, not a paraphrase.
         */
        if (row.stored_status === 'pending_claim' && row.status === 'expired') {
          return (
            <Tooltip title={data ? data.expiry_note : ''}>
              <Chip
                size="small"
                variant="outlined"
                color="error"
                label="expired (lapsed)"
                data-testid={`claim-lapsed-${row.id}`}
              />
            </Tooltip>
          );
        }
        const meta = STATUS_CHIP[row.status];
        return (
          <Chip
            size="small"
            variant="filled"
            color={meta ? meta.color : 'default'}
            label={meta ? meta.label : row.status}
            data-testid={`claim-status-${row.id}`}
          />
        );
      },
    },
    {
      id: 'age_days',
      label: 'Age',
      align: 'right',
      render: (row) => (
        <Tooltip title={`Bought ${shortDate(row.created_at)}`}>
          <Typography variant="body2" color="text.secondary">{num(row.age_days)}d</Typography>
        </Tooltip>
      ),
    },
    {
      id: 'claim_token_expires_at',
      label: 'Token expires',
      sortable: true,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {shortDate(row.claim_token_expires_at)}
        </Typography>
      ),
    },
    {
      id: 'claimed_at',
      label: 'Claimed',
      sortable: true,
      render: (row) => (
        <Box>
          <Typography variant="body2" color="text.secondary">
            {shortDate(row.claimed_at)}
          </Typography>
          {row.wedding_code && (
            <Typography variant="caption" color="text.secondary">
              {row.wedding_code}
              {row.partner1_name ? ` · ${row.partner1_name} & ${row.partner2_name}` : ''}
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  const unavailable = data !== null && data.available === false;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <ReceiptLongIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Purchase claims</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          component={RouterLink}
          to="/dashboard/wedding/revenue"
          startIcon={<ArrowBackIcon />}
          size="small"
        >
          Back to Revenue
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} size="small">
          Refresh
        </Button>
      </Stack>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Books paid for through a Stripe Payment Link before the buyer had an account. Nothing here
        appears in any revenue figure on this dashboard until the emailed token is redeemed.
      </Typography>

      {unavailable ? (
        <EmptyState
          icon="receipt"
          title="Purchase claims are not on this database"
          description={(data && data.reason) || 'The table that records them has not been created here.'}
        />
      ) : (
        <>
          {summary && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>{centsToUsd(summary.by_status.pending_claim.cents)}</strong>
              {` still claimable across ${summary.by_status.pending_claim.count} purchase`}
              {summary.by_status.pending_claim.count === 1 ? '' : 's'}
              {summary.by_status.expired.count > 0
                ? `, and ${centsToUsd(summary.by_status.expired.cents)} lapsed across ${summary.by_status.expired.count}.`
                : '.'}
              {' All time — the totals above are the whole table, not the filtered list below.'}
            </Typography>
          )}

          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            {STATUS_PILLS.map((pill) => (
              <Chip
                key={pill.key}
                label={
                  counts
                    ? `${pill.label} (${pill.key === 'all' ? counts.all : counts[pill.key]})`
                    : pill.label
                }
                color={status === pill.key ? 'primary' : 'default'}
                variant={status === pill.key ? 'filled' : 'outlined'}
                onClick={() => changeStatus(pill.key)}
                aria-pressed={status === pill.key}
              />
            ))}
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              size="small"
              label="Search buyer"
              placeholder="Buyer email, name or checkout session"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
            />
            <TextField
              select size="small" label="Tier" value={tier}
              onChange={(e) => changeTier(e.target.value as Tier | 'all')}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All tiers</MenuItem>
              {TIERS.map((t) => <MenuItem key={t} value={t}>{TIER_META[t].label}</MenuItem>)}
            </TextField>
          </Stack>

          {error && !loading ? (
            <EmptyState
              icon="error"
              title="Could not load purchase claims"
              description={error}
              action={{ label: 'Retry', onClick: load, variant: 'contained' }}
            />
          ) : (
            <DataTable
              columns={columns}
              data={data ? data.claims : []}
              loading={loading}
              emptyIcon="receipt"
              emptyMessage="No purchase claim matches these filters."
              onSort={(column, direction) => {
                setSortBy(column);
                setSortOrder(direction === 'asc' ? 'ASC' : 'DESC');
                setPage(0);
              }}
              pagination={{
                page,
                rowsPerPage,
                total: data && data.pagination ? data.pagination.total : 0,
                onPageChange: setPage,
                onRowsPerPageChange: (rows) => { setRowsPerPage(rows); setPage(0); },
              }}
            />
          )}

          {data && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
              {data.expiry_note}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default WeddingClaimsPage;
